import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Check, Zap, Crown } from 'lucide-react';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: '₹999',
    desc: 'Perfect for solo founders',
    color: '#3b82f6',
    features: ['500 emails/month', '3 campaigns', 'AI email generation', 'Auto follow-ups', 'CSV upload'],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '₹1,999',
    desc: 'For growing teams',
    color: '#6c47ff',
    popular: true,
    features: ['2,000 emails/month', '10 campaigns', 'AI email generation', 'Auto follow-ups', 'CSV upload', 'Priority support'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹3,999',
    desc: 'For serious outreach',
    color: '#10b981',
    features: ['5,000 emails/month', 'Unlimited campaigns', 'AI email generation', 'Auto follow-ups', 'CSV upload', 'Email tracking', 'Priority support'],
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '₹4,999',
    desc: 'For agencies & teams',
    color: '#f59e0b',
    features: ['10,000 emails/month', 'Unlimited campaigns', 'AI email generation', 'Auto follow-ups', 'CSV upload', 'Email tracking', 'White label', 'Dedicated support'],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loading, setLoading] = useState(false);
  const [activePlan, setActivePlan] = useState(null);

  useEffect(() => {
    api.get('/payments/my-plan').then(r => setCurrentPlan(r.data.plan)).catch(() => {});
  }, []);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    setActivePlan(planId);
    try {
      const { data } = await api.post('/payments/create-order', { plan: planId });

      const options = {
        key: data.key,
        amount: data.order.amount,
        currency: data.order.currency,
        name: 'ColdCraft AI',
        description: `${data.plan.name} Plan - Monthly`,
        order_id: data.order.id,
        handler: async (response) => {
          try {
            await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planId,
            });
            setCurrentPlan(planId);
            alert(`🎉 You are now on the ${data.plan.name} plan!`);
          } catch {
            alert('Payment verification failed. Contact support.');
          }
        },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#6c47ff' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert('Failed to initiate payment. Try again.');
    } finally {
      setLoading(false);
      setActivePlan(null);
    }
  };

  return (
    <div style={{ padding: 32, maxWidth: 1100 }}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.02em' }}>Simple, Transparent Pricing</h1>
        <p style={{ color: 'var(--muted)', marginTop: 8, fontSize: 14 }}>
          {currentPlan !== 'free' ? `You are on the ${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} plan` : 'Start free, upgrade when ready'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
        {plans.map((plan) => {
          const isActive = currentPlan === plan.id;
          return (
            <div key={plan.id} className="card" style={{
              padding: 24,
              border: plan.popular ? `2px solid ${plan.color}` : '1px solid var(--border)',
              position: 'relative',
              transition: 'transform .15s',
            }}>
              {plan.popular && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: plan.color, color: 'white', fontSize: 11, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap'
                }}>MOST POPULAR</div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${plan.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {plan.id === 'agency' ? <Crown size={15} color={plan.color} /> : <Zap size={15} color={plan.color} />}
                </div>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{plan.name}</span>
              </div>

              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-.03em' }}>{plan.price}</span>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>/month</span>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 20 }}>{plan.desc}</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {plan.features.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <Check size={13} color={plan.color} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => !isActive && handleSubscribe(plan.id)}
                disabled={isActive || (loading && activePlan === plan.id)}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  background: isActive ? '#10b981' : plan.color,
                  opacity: loading && activePlan === plan.id ? 0.7 : 1,
                  cursor: isActive ? 'default' : 'pointer',
                }}
              >
                {isActive ? '✓ Current Plan' : loading && activePlan === plan.id ? 'Processing...' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}